import { defineStore } from 'pinia'
import { getIndexerDerivativesApi } from '@shared/service/derivative'
import { UI_DEFAULT_PAGINATION_LIMIT_COUNT } from '@/app/utils/constants'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import type { FundingPayment } from '@injectivelabs/sdk-ts'
import type { ActivityFetchOptions } from '@/types'

type ActivityStoreState = {
  subaccountFundingHistoryCount: number
  subaccountFundingHistory: FundingPayment[]
}

const initialStateFactory = (): ActivityStoreState => ({
  subaccountFundingHistory: [],
  subaccountFundingHistoryCount: 0
})

export const useActivityStore = defineStore('activity', {
  state: (): ActivityStoreState => initialStateFactory(),
  actions: {
    setSubaccountFundingHistory(subaccountFundingHistory: FundingPayment[]) {
      this.subaccountFundingHistory = subaccountFundingHistory
    },

    setSubaccountFundingHistoryCount(subaccountFundingHistoryCount: number) {
      this.subaccountFundingHistoryCount = subaccountFundingHistoryCount
    },

    async fetchSubaccountFundingHistory(options?: ActivityFetchOptions) {
      const accountStore = useAccountStore()
      const activityStore = useActivityStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const filters = options?.filters
      const pagination = options?.pagination || {
        skip: 0,
        limit: UI_DEFAULT_PAGINATION_LIMIT_COUNT
      }
      const marketIds = filters?.marketIds || derivativeStore.activeMarketIds
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const {
        pagination: fundingPagination,
        fundingPayments: subaccountFundingHistory
      } = await apiCallsRequestCache.cached({
        key: createRequestKey(CLIENT_REQUEST_KEYS.fundingHistory, {
          pagination: {
            skip: pagination.skip,
            limit: pagination.limit
          },
          marketIds: [...marketIds].sort(),
          subaccountId: accountStore.subaccountId
        }),
        request: () =>
          indexerDerivativesApi.fetchFundingPayments({
            marketIds,
            pagination,
            subaccountId: accountStore.subaccountId
          })
      })

      activityStore.setSubaccountFundingHistory(subaccountFundingHistory)
      activityStore.setSubaccountFundingHistoryCount(fundingPagination.total)
    }
  }
})
